import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Link as MUILink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Home, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { httpClient, getErrorMessage } from '@/utils/httpClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserRole {
  id: string;
  name: string;
  type: string;
}

interface ManagedUser {
  id: string;
  username: string;
  email: string;
  display_name: string | null;
  auth_provider: string;
  auth_provider_id: string | null;
  user_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  roles: UserRole[];
  last_seen_at: string | null;
}

type UserSortField =
  | 'display_name'
  | 'username'
  | 'email'
  | 'auth_provider'
  | 'user_type'
  | 'status'
  | 'last_seen_at'
  | 'created_at';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns MUI Chip sx overrides for roles that have a specific brand colour.
 * The `service-member` role is rendered as a solid blue chip to match the
 * Figma prototype and make it visually distinct from standard roles.
 */
const getRoleChipSx = (roleName: string): Record<string, unknown> | undefined => {
  switch (roleName.toLowerCase()) {
    case 'service-member':
      return { backgroundColor: '#1565C0', color: '#fff', borderColor: '#1565C0' };
    default:
      return undefined;
  }
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAuthProvider = (provider: string | null | undefined): string => {
  switch (provider?.toLowerCase()) {
    // Federated identity providers (Azure Workforce / Entra, incl. legacy CIAM).
    case 'azure-workforce':
    case 'azure-ciam':
    case 'entra':
      return 'SSO';
    default:
      return 'Local';
  }
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'error';
    case 'inactive':
      return 'warning';
    default:
      return 'default';
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<UserSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  usePageTitle('User Management');

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const data = await httpClient.get<ManagedUser[]>('/api/rest/app/v1/admin/users?limit=500');
      setUsers(data);
    } catch (err) {
      setErrorMessage(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  // Client-side filter for instant feedback
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.display_name ?? '').toLowerCase().includes(q),
    );
  }, [users, search]);

  const sortedUsers = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'display_name':
          aVal = (a.display_name ?? '').toLowerCase();
          bVal = (b.display_name ?? '').toLowerCase();
          break;
        case 'username':
          aVal = a.username.toLowerCase();
          bVal = b.username.toLowerCase();
          break;
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'auth_provider':
          aVal = formatAuthProvider(a.auth_provider).toLowerCase();
          bVal = formatAuthProvider(b.auth_provider).toLowerCase();
          break;
        case 'user_type':
          aVal = a.user_type.toLowerCase();
          bVal = b.user_type.toLowerCase();
          break;
        case 'status':
          aVal = a.status.toLowerCase();
          bVal = b.status.toLowerCase();
          break;
        case 'last_seen_at':
          aVal = a.last_seen_at ?? '';
          bVal = b.last_seen_at ?? '';
          break;
        case 'created_at':
          aVal = a.created_at;
          bVal = b.created_at;
          break;
        default:
          return 0;
      }
      if (aVal === bVal) return 0;
      const result = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filtered, sortField, sortDirection]);

  const handleSort = useCallback(
    (field: UserSortField) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField],
  );

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">User Management</Typography>
        </Breadcrumbs>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          All provisioned users
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search by name, username, or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Error */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {sortedUsers.length} {sortedUsers.length === 1 ? 'user' : 'users'}
            {sortedUsers.length !== users.length ? ` (filtered from ${users.length})` : ''}
          </Typography>

          <Card>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'display_name'}
                        direction={sortField === 'display_name' ? sortDirection : 'asc'}
                        onClick={() => handleSort('display_name')}
                      >
                        Display Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'username'}
                        direction={sortField === 'username' ? sortDirection : 'asc'}
                        onClick={() => handleSort('username')}
                      >
                        Username
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'email'}
                        direction={sortField === 'email' ? sortDirection : 'asc'}
                        onClick={() => handleSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'auth_provider'}
                        direction={sortField === 'auth_provider' ? sortDirection : 'asc'}
                        onClick={() => handleSort('auth_provider')}
                      >
                        Source Auth
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'user_type'}
                        direction={sortField === 'user_type' ? sortDirection : 'asc'}
                        onClick={() => handleSort('user_type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'status'}
                        direction={sortField === 'status' ? sortDirection : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'last_seen_at'}
                        direction={sortField === 'last_seen_at' ? sortDirection : 'asc'}
                        onClick={() => handleSort('last_seen_at')}
                      >
                        Last Activity
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'created_at'}
                        direction={sortField === 'created_at' ? sortDirection : 'asc'}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {search ? 'No users match your search.' : 'No users found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.display_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          {user.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatAuthProvider(user.auth_provider)}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.roles.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            user.roles.map((role) => {
                              const roleSx = getRoleChipSx(role.name);
                              return (
                                <Chip
                                  key={role.id}
                                  label={role.name}
                                  size="small"
                                  color="primary"
                                  variant={roleSx ? 'filled' : 'outlined'}
                                  sx={roleSx}
                                />
                              );
                            })
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {user.user_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          size="small"
                          color={statusColor(user.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatDate(user.last_seen_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatDate(user.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </Container>
  );
};
